import attackReactionRows from '../../../catalogs/janus/attack-reaction-row-contract.json';
import { assignFreshStatusIds } from './status-row-identity.js';
import { resolveWeaponAttack, weaponAttackRollType, type WeaponAttackInput, type WeaponAttackReaders } from './weapon-attack.js';
import { resolveAttackDamageReactions, applyAttackDamageReactions, attackDamageFrame, resolvedVerdict, type AbilityUseReaders, type AbilityUseVerdict } from './ability-use.js';
import type { RollSource } from './roll-source.js';
import { activeStatuses, ec, ecId, ecLean, HandBack, Refusal, itemField, record, scalar, upper, type JanusCtx } from '../ability/context.js';
import { frameCopyOnRead } from '../ability/frame-snapshot.js';
import { revealAfterAction } from '../ability/stealth-hide.js';
import { takeRevealedAttackAdv } from '../ability/stealth-reveal-carry.js';
import { attackStatusHasOnlyRuntimeLayers, attackStatusPlanFailure, projectAttackStatuses, type AttackStatusProofs } from './attack-status-projection.js';
import { createAbilityConditionEvaluator } from '../compile/ability-conditions.js';
import { C1_CONTRACT } from '../compile/possessor-compile.js';
import { findInventoryRow } from '../inventory/flag-adjust.js';
import { statusScalar } from './activity.js';
import { statusItemId, statusRows, statusRemovalBefore, STATUS_IDS } from '../statuses/expiry.js';
import { consentPending, isPlayerController } from '../ability/trigger-consent';
import { applyUniversalFortunateOnLuckGain } from '../ability/reaction-inventory.js';
import { isFortunateStatus } from './weapon-attack-fortunate.js';
import type { WeaponAttackCommand } from './weapon-attack-commit';
import { posCoord } from '../../shared/array-types';
import { nextRollAgainstRider, rollTypeModifierStacks, SKILL_ROLL_ALIAS } from '../ability/skill-roll';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers';
import { layerListEntryUuid } from '../ability/statuses/status-trigger-gate';
import { postRollReactionsPreDraw, runPostRollReduce } from '../ability/post-roll-reactions.js';
import { planDamageStatusEffects, targetDamageReductions } from '../ability/rolls/damage-type-roll.js';
import { connectionSurvivors, planStoredStatusRemoval } from '../ability/statuses/status-effect-removal-helpers.js';
import { absorbTemporaryVitality, temporaryVitality } from '../ability/rolls/temporary-vitality.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';
import { applyExplicitWeaponHitEffects, spendExplicitWeaponCharges } from './explicit-weapon-attack.js';
import { triggerFamilyPreDraw, runTriggerFamily } from '../ability/trigger-executor.js';
import { containsTriggerKey, EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL } from './weapon-attack-extra-damage.js';
import { isAttackPassiveWindow } from '../compile/status-attack-budget.js';
import { damageHealsCaster } from '../ability/rolls/healing-roll.js';
import { statusItemPassiveEntries } from '../ability/rolls/status-item-passive-entries.js';

type Row = Record<string, unknown>;
type Scope = ReturnType<typeof resolveWeaponAttack>;
export type AttackReactionEffects = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
export interface AttackFrameWork {
  beforeSwing(ctx: JanusCtx): boolean | void;
  afterSwing(ctx: JanusCtx, terminal: boolean): void;
}
export const ATTACK_REACTION_EC_COLUMNS = ['luck', 'aps', 'reaction', 'movement_pct', 'v_current', 'v_max', 'v_temp', 'v_max_temp', 'preferred_speed_type', 'encounter_creature_dead', 'x_pos', 'y_pos', 'z_pos'] as const;
export const ATTACK_REACTION_LOG_TYPES: readonly string[] = attackReactionRows.allowed;
export const ATTACK_REACTION_STATUS_COLUMNS = ['status_id', 'status_item_id', 'target', 'caster', 'status_connected_id', 'status_stack', 'ends',
  'status_ends_on_target_start_of_turn', 'status_ends_on_target_end_of_turn', 'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn',
  'status_aoe_type', 'status_aoe_distance', 'status_aoe_distance_depth', 'status_aoe_distance_height', 'status_aoe_source',
  'status_origin_point_x', 'status_origin_point_y', 'status_origin_point_z', 'status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z',
  'status_origin_id', 'status_casting_targets_arr', 'status_opposing_team_only', 'status_damage_type', 'status_forced_roll_extreme'] as const;
const no = (reason: string): Scope => ({ supported: false, refused: false, reason });

/** A route consequence can finish before the strike. Keep its frame without inventing an attack roll. */
function skippedSwing(attacker: Row): Extract<Scope, { supported: true }> {
  return { supported: true, draws: [],
    deltas: { attacker: { aps: Number(ecLean(attacker, 'aps') ?? 0), weapon_attack: Number(ecLean(attacker, 'weapon_attack') ?? 0) },
      target: {}, serverFollowUp: [] },
    outcome: { swingSkipped: true, opportunityAttack: true, naturalD20: 0, d20Draws: [], advDisStacks: 0, advDisSources: [],
      skillTierDraw: null, skillTierDie: null, modifier: 0, result: 0, dc: 0, fullAc: 0, hit: false, autoMiss: false, critical: false,
      criticalVariant: null, criticalBonus: 0, cascadeTriggers: [], maimedSave: null, fortunateFired: false, criticalTuples: [],
      damageDraws: [], damageExplosions: [], damage: null, extraDamage: [], damageAdjustments: [], damageFeedback: null,
      damageBeforeMitigation: null, mitigation: [], reactionFired: null, untrained: false, evadeBlockedByStatus: null,
      death: null, statusesApplied: [], luckSpent: false, luckAfter: null, rider: null } };
}

/** Serialize the frozen consequence frame with the same held scalar reader as the page.
 * Listener-only participants still need their revision even when a reaction was declined. */
export function attackReactionCommand(effects: AttackReactionEffects, roster: readonly Row[],
  field: (row: Row | undefined, key: string) => unknown): Pick<WeaponAttackCommand, 'reaction_effects' | 'reaction_expected_revs' | 'reaction_member_before' | 'teleport_offers'> {
  const { rows, ec, statusesAdded, statusesRemoved, statusStacks, charges, chargesRemoved, playerCharactersChanged, spawnRows, statusTriggerListeners } = effects;
  const idOf = (row: Row): string => String(field(row, 'encounter_creature_id') ?? '').toUpperCase();
  const number = (value: unknown): number => Number.isFinite(Number(value)) ? Number(value) : 0;
  const ids = new Set([...Object.keys(ec), ...rows.flatMap(row => [row.roll_by, row.roll_to].filter((id): id is string => !!id)),
    ...statusesAdded.flatMap(status => [status.target, status.caster]), ...charges.map(charge => charge.holder), ...(chargesRemoved ?? []).map(charge => charge.holder),
    ...statusesRemoved.map(status => status.target), ...statusStacks.map(status => status.target),
    ...statusTriggerListeners?.map(listener => listener.reactorId) ?? []]);
  return {
    ...(effects.teleportOffers?.length ? { teleport_offers: effects.teleportOffers } : {}),
    reaction_effects: { rows, ec, statusesAdded: statusesAdded.map(status => Object.fromEntries(Object.entries(status)
      .filter(([key]) => ATTACK_REACTION_STATUS_COLUMNS.includes(key as typeof ATTACK_REACTION_STATUS_COLUMNS[number]))) as typeof status),
      statusesRemoved, statusStacks, charges, ...(chargesRemoved?.length ? { chargesRemoved } : {}), playerCharactersChanged, ...(spawnRows ? { spawnRows } : {}),
      ...(statusTriggerListeners?.length ? { statusTriggerListeners } : {}) },
    reaction_expected_revs: Object.fromEntries(roster.filter(row => ids.has(idOf(row))).map(row => [idOf(row), number(field(row, 'movement_rev'))])),
    reaction_member_before: Object.fromEntries(Object.entries(ec).map(([id, changes]) => {
      const member = roster.find(row => idOf(row) === id);
      return [id, Object.fromEntries(Object.keys(changes).map(key => [key, key === 'preferred_speed_type'
        ? String(field(member, key) ?? '') : ['x_pos', 'y_pos', 'z_pos'].includes(key) ? number(member ? posCoord(member, key as 'x_pos' | 'y_pos' | 'z_pos') ?? field(member, key) : 0) : number(field(member, key))]))];
    })),
  };
}

/** One transport contract, checked both inside independent kill effects and for the combined consequence frame. */
function reactionTransportFailure(effects: AttackReactionEffects, form = false): string | null {
  if (effects.inventory.length || form && effects.chargesRemoved?.length || effects.deathTail.length || effects.dyingTail?.length || effects.concentrationBreaks.length || effects.summons?.length
    || effects.turnTransition || effects.turnDeathNone || effects.chargedConsume || effects.timedInventory || effects.monsterBitRemovals?.length
    || (!form && effects.shapeshiftInventory?.length))
    return 'attack:reactions:continuation-out-of-scope';
  if (form && (effects.fortunateOffers?.length || effects.statusTriggerListeners?.length))
    return 'attack:reactions:write-kind-out-of-scope';
  const column = Object.values(effects.ec).flatMap(Object.keys).find(key => !ATTACK_REACTION_EC_COLUMNS.includes(key as typeof ATTACK_REACTION_EC_COLUMNS[number]));
  if (column) return 'attack:reactions:column-out-of-scope:' + column;
  const log = effects.rows.find(row => !(form && row.roll_type_1 === 'lost_shapeshift')
    && !ATTACK_REACTION_LOG_TYPES.includes(row.roll_type_1));
  if (log) return 'attack:reactions:log-out-of-scope:' + log.roll_type_1;
  for (const added of effects.statusesAdded)
    if (Object.entries(added).some(([key, value]) => !ATTACK_REACTION_STATUS_COLUMNS.includes(key as typeof ATTACK_REACTION_STATUS_COLUMNS[number])
      && value !== null && value !== undefined && value !== 0 && value !== '')) return 'attack:reactions:status-shape-out-of-scope';
  return null;
}

export function pinAttackReactionRemovals(effects: Pick<AttackReactionEffects, 'statusesRemoved'>, members: ReadonlyMap<string, Row>,
  read: Pick<AbilityUseReaders, 'statuses'>): string | null {
  for (const removed of effects.statusesRemoved) {
    const member = members.get(removed.target);
    const original = member ? read.statuses(member).filter(status => removed.status_id
      ? String(statusScalar(status, 'status_id') ?? '').toUpperCase() === removed.status_id.toUpperCase() : statusItemId(status) === removed.status_item_id.toUpperCase()) : [];
    if (original.length !== 1) return 'attack:reactions:status-removal-not-held';
    const status = original[0]!;
    Object.assign(removed, { status_id: String(statusScalar(status, 'status_id') ?? '').toUpperCase(),
      before: statusRemovalBefore(status, removed.target) });
  }
  return null;
}

function reactionRecorderFailure(effects: AttackReactionEffects, members: ReadonlyMap<string, Row>, read: AbilityUseReaders, proofs?: AttackStatusProofs): string | null {
  const failure = reactionTransportFailure(effects) ?? pinAttackReactionRemovals(effects, members, read);
  if (failure) return failure;
  for (const [id, member] of members) {
    // Complete death/Dying posture is saved truth. The incremental status-only display
    // compiler cannot project held drops; the committed projection recovers from C5 raw rows.
    if (effects.ec[id]?.encounter_creature_dead === 1 || Number(effects.ec[id]?.v_current ?? ecLean(member, 'v_current')) === 0
      && (effects.statusesAdded.some(status => status.target === id && status.status_item_id === STATUS_IDS.dying)
        || read.statuses(member).some(status => statusItemId(status) === STATUS_IDS.dying))) continue;
    const gap = attackStatusPlanFailure(member, effects, read.definition, members, read.possessorScene, proofs);
    // An actual Body-changing status is saved truth too; the committed raw-row projection recovers it.
    if (gap && gap !== 'attack:status-projection:unported:status-ac:body') return gap;
  }
  return null;
}

/** A priced walk must know whether its held damage statuses can change its geometry or speed.
 * Inspect those exact live statuses; a hypothetical killing roll never enters this preflight. */
function movementDamagePreflight(ctx: JanusCtx, caster: Row, target: Row,
  check: (effects: AttackReactionEffects) => string | null): string | null {
  const plan = planDamageStatusEffects(ctx, caster, target);
  const selected = [...plan.reveal];
  if (plan.concentrating) {
    const connection = upper(statusScalar(plan.concentrating, 'status_connected_id'));
    selected.push(...connectionSurvivors(ctx, connection, '').map(peer => peer.status));
  }
  // an escaped charm ends its whole connection (the used / Charmed rows are on it, or are the row itself when it has none)
  if (plan.charm?.kind === 'contest') {
    const { charmId, usedId, connection } = plan.charm;
    selected.push(...(connection ? connectionSurvivors(ctx, connection, '').map(peer => peer.status)
      : activeStatuses(ctx, target).filter(status => [charmId, usedId].includes(upper(statusScalar(status, 'status_id'))))));
  }
  const removalIds = new Set([...(plan.wake?.statusIds ?? []), ...(plan.retry?.statusIds ?? [])]);
  for (const status of activeStatuses(ctx, target)) {
    if (!removalIds.has(upper(statusScalar(status, 'status_id')))) continue;
    const { peers, lone } = planStoredStatusRemoval(ctx, target, status);
    selected.push(status, ...peers.map(peer => peer.status), ...(lone ? [lone.status] : []));
  }
  const effects = resolvedVerdict(ctx);
  effects.statusesRemoved = selected.map(status => ({ status_id: upper(statusScalar(status, 'status_id')),
    status_item_id: statusItemId(status), target: upper(statusScalar(status, 'status_target_id')) || ecId(target) }));
  return check(effects);
}

/** Resolve the swing once; compose death only for its actual victim and actual damage. */
export function resolveWeaponAttackWithReactions(input: WeaponAttackInput, encounter: Row, attacker: Row, target: Row,
  read: WeaponAttackReaders, reactionRead: AbilityUseReaders, roll: RollSource,
  checkEffects?: (effects: AttackReactionEffects) => string | null, work?: AttackFrameWork,
  boundAbility?: Row): Scope & { reactionEffects?: AttackReactionEffects; damageEffects?: AttackReactionEffects } {
  const projectionProofs: AttackStatusProofs = new WeakMap();
  const conditions = createAbilityConditionEvaluator({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
    aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
    resolveCarrierRef: id => { const def = reactionRead.definition(id); if (!def) throw new Error('attack:reaction-condition-definition-not-held:' + id); return def; },
    resolveInventoryInstance: id => {
      for (const row of (encounter.encounter_creatures_arr ?? []) as Row[]) { const found = findInventoryRow(row, id); if (found) return found; }
      return null;
    }, queryTemplateDefinition: reactionRead.definition,
    equipmentSlots: C1_CONTRACT.equipmentSlots, vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
    conditionSource: kind => { throw new HandBack('attack:reaction-condition-context-not-held:' + kind); } });
  const boundId = boundAbility ? upper(itemField(boundAbility, 'item_id')) : '';
  const gatedRead = { ...reactionRead,
    definition: (id: string) => boundId && boundId !== upper(input.weaponId) && upper(id) === boundId ? boundAbility!
      : upper(id) === upper(input.weaponId) ? read.definition(input.weaponId) : reactionRead.definition(id),
    triggerConditionsMet: (ability: Row, caster: Row) => conditions.check(ability, caster),
    killTriggerProofs: new Map<string, { served: boolean; failure: string | null }>(),
    killTriggerPreflightFailure: (ability: Row): string | null => {
      const type = upper(scalar(reactionRead.value(ability, 'roll_type')));
      // Both authored aliases dispatch through noRoll(), whose emitted log type is the supported string `no_roll`.
      const logged = ['E26FC164-16F5-E38B-66F9-337FAEDE2A67', '28B4E29C-5D8D-57CC-7634-761CEBD01190'].includes(type) ? 'no_roll' : type;
      return logged && !ATTACK_REACTION_LOG_TYPES.includes(logged as typeof ATTACK_REACTION_LOG_TYPES[number])
        ? 'attack:reactions:log-out-of-scope:' + type : null;
    } };
  const originals = new Map<string, Row>(((encounter.encounter_creatures_arr ?? []) as Row[]).map(row => [ecId(row), row]));
  originals.set(input.actorId, attacker);
  originals.set(input.targetId, target);
  const concealed = reactionRead.statuses(attacker).some(status => ['47667406-1B72-9D60-310B-D8CF6BFE397D', '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD'].includes(statusItemId(status)));
  const shiftedMember = [...originals.values()].some(member => Number(reactionRead.value(member, 'shapeshifted') ?? 0) === 1);
  const provenStrays = new Map<string, string | null>();
  const scopedRead: WeaponAttackReaders = { ...read, strayTargetPreflight: candidate => {
    const prior = read.strayTargetPreflight?.(candidate); if (prior) return prior;
    const id = ecId(candidate); if (provenStrays.has(id)) return provenStrays.get(id)!;
    const gate = resolveAttackDamageReactions({ actorId: input.actorId, targetId: id, damageTypes: [read.damageTypeId(read.definition(input.weaponId) ?? {})],
      damage: true, damageTotal: 0, weaponId: input.weaponId, zeroVitality: false, targetJustKilled: false, preview: true, rollPrivate: input.rollPrivate,
        ...(!input.opportunityAttack ? { postRoll: { rollType: weaponAttackRollType(input, read.definition(input.weaponId) ?? {}, read), hit: false } } : {}) },
      encounter, originals, gatedRead, { d: () => { throw new Error('attack:stray-preview-drew'); }, fraction: () => { throw new Error('attack:stray-preview-drew'); } });
    if (gate.kind !== 'resolved') { const reason = 'reason' in gate ? gate.reason : gate.message; provenStrays.set(id, reason); return reason; }
    if (consentPending(gatedRead)) return 'preflight:trigger-consent';
    provenStrays.set(id, null); return null;
  } };
  const run = (source: RollSource): Scope & { reactionEffects?: AttackReactionEffects; damageEffects?: AttackReactionEffects } => {
    const copy = frameCopyOnRead(encounter, originals, { catalogs: 'share' });
    const ctx: JanusCtx = { ...attackDamageFrame(copy.encounter, copy.members, gatedRead, source, input.rollPrivate, copy),
      encounter: copy.encounter, members: copy.members, originals, teleportOffers: [],
      read: { ...gatedRead, killTriggerRecorder: frame => () => reactionRecorderFailure(resolvedVerdict(frame), originals, reactionRead, projectionProofs) } };
    const current = ctx.members, swingEncounter = ctx.encounter;
    const statusKey = (rows: Row[]): string => rows.map(status =>
      `${upper(statusScalar(status, 'status_id'))}:${statusItemId(status)}:${readStatusRowStack(status)}`).join('|');
    const projectedStatuses = new Map<string, string>();
    const finish = (scope: Scope & { damageEffects?: AttackReactionEffects }): Scope & { reactionEffects?: AttackReactionEffects; damageEffects?: AttackReactionEffects } => {
      if (!scope.supported) return scope;
      try { work?.afterSwing(ctx, scope.outcome.death !== null); }
      catch (error) {
        if (consentPending(ctx.read)) return no('outcome:trigger-consent');
        if (error instanceof HandBack) return no(error.reason);
        if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
        throw error;
      }
      const victimId = scope.outcome.stray?.targetId ?? input.targetId, victim = current.get(victimId);
      if (!victim) return no('attack:reaction-victim-not-held');
      assignFreshStatusIds(ctx);
      const effects = resolvedVerdict(ctx);
      // Primary scalars carry their final luck; all other reaction/resource/PC/position writes stay in this shared frame.
      for (const id of [input.actorId, victimId]) {
        const member = current.get(id)!;
        const luck = Number(ecLean(member, 'luck') ?? 0), before = Number(ecLean(originals.get(id)!, 'luck') ?? 0);
        const primary = id === input.actorId ? scope.deltas.attacker : scope.deltas.target;
        if (luck !== before) primary.luck = luck; else delete primary.luck;
        if (effects.ec[id]) delete effects.ec[id]!.luck;
      }
      // A reaction can return economy to its original before-value after the primary debit. Persist that
      // explicit final value whenever the primary write would otherwise erase the intervening change.
      for (const [id, primary, columns] of [[input.actorId, scope.deltas.attacker, ['aps', 'reaction']],
        [victimId, scope.deltas.target, ['v_current', 'v_temp', 'reaction', 'encounter_creature_dead']]] as const) {
        const member = current.get(id)!;
        for (const key of columns) {
          if (effects.spawnRows?.writes.some(write => write.table === 'encounter_creature_turn_resets'
            && write.row.encounter_creature_id === id && key in write.row)) continue;
          const planned = (primary as Record<string, unknown>)[key];
          if (planned !== undefined) {
            const final = Number(ecLean(member, key) ?? 0);
            if (final !== planned) (effects.ec[id] ??= {})[key] = final;
            else if (key === 'v_temp' && effects.ec[id]) delete effects.ec[id]!.v_temp;
          }
        }
      }
      for (const [id, changes] of Object.entries(effects.ec)) if (!Object.keys(changes).length) delete effects.ec[id];
      if (effects.spawnRows?.writes.some(write => write.table === 'encounter_creatures'
          && write.row.encounter_creature_id === victimId && write.row.encounter_creature_deleted === 1))
        delete scope.deltas.target.encounter_creature_dead;
      const consequenceFailure = checkEffects?.(effects);
      if (consequenceFailure) return no(consequenceFailure);
      const recorderFailure = reactionRecorderFailure(effects, originals, reactionRead, projectionProofs);
      if (recorderFailure) return no(recorderFailure);
      return effects.rows.length || Object.keys(effects.ec).length || effects.statusesAdded.length || effects.statusesRemoved.length
        || effects.statusStacks.length || effects.charges.length || effects.chargesRemoved?.length || effects.playerCharactersChanged.length || effects.statusTriggerListeners?.length
        || effects.spawnRows?.writes.length || effects.fortunateOffers?.length || effects.teleportOffers?.length ? { ...scope, reactionEffects: effects } : scope;
    };
    let swing = true;
    try { swing = work?.beforeSwing(ctx) !== false; }
    catch (error) {
      if (consentPending(ctx.read)) return no('outcome:trigger-consent');
      if (error instanceof HandBack) return no(error.reason);
      if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
      throw error;
    }
    if (!swing) return finish(skippedSwing(originals.get(input.actorId)!));
    // Non-damage trigger payloads belong to this consequence frame. Merely
    // holding Precision Rhythm must not grant its future window's advantage.
    const swingPassives = read.entries(attacker, 'passive_ability').filter(raw => {
      const entry = record(raw), def = record(entry?.item_arr) ?? reactionRead.definition(layerListEntryUuid(raw));
      return !def || !containsTriggerKey(def)
        || [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL].includes(upper(read.value(def, 'roll_type')));
    });
    let swingRead: WeaponAttackReaders = { ...scopedRead,
      entries: (row, key) => ecId(row) === input.actorId && key === 'passive_ability' ? swingPassives : read.entries(row, key) };
    const currentAttacker = current.get(input.actorId)!, currentTarget = current.get(input.targetId)!;
    const weapon = read.definition(input.weaponId);
    if (!weapon) return no('weapon-definition-missing');
    const rollType = weaponAttackRollType(input, weapon, read);
    const casterRollEvent = { trigger: 'triggers_when_caster_makes_roll_type', rollType,
      excludeAbilitiesWithLayer: ['triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit'],
      excludeAbilityRollTypes: [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL] };
    let priced = false, paidAbilitySettled = false;
    if (!input.opportunityAttack) swingRead = { ...swingRead,
      postRollPreDraw: (caster, victim, swingRollType) => {
        const members = new Map(current).set(ecId(caster), caster).set(ecId(victim), victim);
        postRollReactionsPreDraw({ ...ctx, members }, caster, victim, swingRollType);
        if (consentPending(ctx.read)) throw new HandBack('preflight:trigger-consent');
      },
      postRollReduce: (caster, victim, swingRollType) => {
        const reactor = current.get(ecId(victim))!, inciting = current.get(ecId(caster))!;
        Object.assign(ec(reactor), ec(victim)); Object.assign(ec(inciting), ec(caster));
        priced = true;
        runPostRollReduce(ctx, inciting, reactor, swingRollType);
        return { reductions: targetDamageReductions(ctx, reactor), reaction: Number(ecLean(reactor, 'reaction') ?? 0),
          vitality: Number(ecLean(reactor, 'v_current') ?? 0), attackerLuck: Number(ecLean(inciting, 'luck') ?? 0) };
      } };
    if (concealed) {
      const caster = currentAttacker;
      try {
        // Bind the actual swing's roll type (including throws); all removal/connection semantics stay in the ability helper.
        revealAfterAction(ctx, { abilityId: input.weaponId, casterId: input.actorId, targetIds: [input.targetId],
          rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0,
          useAbilityVariable1: null, useAbilityVariable2: null },
          { ...weapon, item_layer_types: { ...record(weapon.item_layer_types), roll_type: rollType } }, caster);
        const reveal = resolvedVerdict(ctx);
        // A loss listener needing another write/roll must be proved by a whole-action executor before this lane can own it.
        if (reveal.rows.length || Object.keys(reveal.ec).length || reveal.statusesAdded.length || reveal.charges.length
          || reveal.inventory.length || reveal.playerCharactersChanged.length || reveal.statusStacks.length || reveal.deathTail.length
          || reveal.concentrationBreaks.length || reveal.statusTriggerListeners?.length || reveal.fortunateOffers?.length
          || reveal.turnTransition || reveal.turnDeathNone || reveal.chargedConsume || reveal.timedInventory || reveal.shapeshiftInventory?.length)
          return no('attack:reveal:status-loss-effects-out-of-scope');
        const gap = pinAttackReactionRemovals(reveal, originals, reactionRead);
        if (gap) return no(gap);
        // A same-connection peer may supply cached stats. Do not swing with its ended essence/skill/equipment contribution.
        if (reveal.statusesRemoved.some(s => !attackStatusHasOnlyRuntimeLayers(reactionRead.definition(s.status_item_id))))
          return no('attack:reveal:derived-status-peer-out-of-scope');
        swingRead = { ...swingRead, revealedAttackAdv: takeRevealedAttackAdv(ctx, caster, rollType, rollType),
          entries: (row, key) => row === caster && key === 'passive_ability' ? swingPassives : read.entries(row, key) };
      } catch (error) {
        if (error instanceof HandBack) return no('attack:reveal:' + error.reason);
        if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
        throw error;
      }
    }
    // Concealment's removal proof must precede unrelated trigger writes. Both
    // ordinary and opportunity swings retain these writes in the same frame.
    if (input.explicitInventoryWeapon) {
      try {
        triggerFamilyPreDraw(ctx, currentAttacker, casterRollEvent, undefined, currentTarget);
        runTriggerFamily(ctx, casterRollEvent, currentAttacker, currentTarget, [currentAttacker], false);
      } catch (error) {
        if (error instanceof HandBack) return no(error.reason);
        if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
        throw error;
      }
    }
    let damageEffects: AttackReactionEffects | undefined;
    if (shiftedMember) swingRead = { ...swingRead, absorbFormDamage: (victim, damage) => {
      // Form writes start after the hit's reduction costs and share its live members, not its save ledger.
      const before = new Map([...current].map(([id, row]) => [id, { ...row, encounter_creatures: { ...ec(row) } }]));
      const addedBefore = ctx.statusesAdded.length, removedBefore = ctx.statusesRemoved.length;
      const form: JanusCtx = { encounter: swingEncounter, members: current, originals: before, read: ctx.read,
        roll: source, rows: [], statusesAdded: ctx.statusesAdded.slice(),
        statusesRemoved: ctx.statusesRemoved.slice(), chargesChanged: [], inventoryChanged: [],
        statusConnectedId: null, statusStacks: [], rollPrivate: input.rollPrivate ?? 0, shapeshiftInventory: [], shapeshiftActorId: input.actorId };
      const overflow = absorbTemporaryVitality(form, victim, damage, temporaryVitality(victim));
      const effects = resolvedVerdict(form);
      effects.statusesAdded = effects.statusesAdded.slice(addedBefore);
      effects.statusesRemoved = effects.statusesRemoved.slice(removedBefore);
      if (!damageEffects) damageEffects = effects;
      else {
        for (const [id, changes] of Object.entries(effects.ec)) Object.assign(damageEffects.ec[id] ??= {}, changes);
        for (const key of ['rows', 'statusesAdded', 'statusesRemoved', 'statusStacks', 'charges', 'playerCharactersChanged',
          'inventory', 'deathTail', 'concentrationBreaks'] as const)
          (damageEffects[key] as unknown[]).push(...effects[key]);
        damageEffects.shapeshiftInventory = [...(damageEffects.shapeshiftInventory ?? []), ...(effects.shapeshiftInventory ?? [])];
      }
      return overflow;
    } };
    swingRead = { ...swingRead, damageReactionPreflight: (actor, victim, evadeSpent, damageTypes, form) => {
      const members = new Map(current), actorId = ecId(actor), targetId = ecId(victim);
      members.set(actorId, input.opportunityAttack ? { ...actor, encounter_creatures: { ...record(actor.encounter_creatures), reaction: 0 } } : actor);
      members.set(targetId, evadeSpent ? { ...victim, encounter_creatures: { ...record(victim.encounter_creatures), reaction: 0 } } : victim);
      const noDraw = (): never => { throw new Error('attack:state-reaction-preview-drew'); };
      if (form?.reverts) {
        const copy = frameCopyOnRead(swingEncounter, members, { catalogs: 'share' });
        const ctx: JanusCtx = { ...copy, originals: members, read: gatedRead, roll: { d: noDraw, fraction: noDraw }, dryRun: true,
          rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
          statusConnectedId: null, rollPrivate: input.rollPrivate ?? 0, shapeshiftInventory: [], shapeshiftActorId: input.actorId };
        const previewVictim = ctx.members.get(targetId)!;
        absorbTemporaryVitality(ctx, previewVictim, temporaryVitality(previewVictim), temporaryVitality(previewVictim));
        const effects = resolvedVerdict(ctx);
        for (const changes of Object.values(effects.ec)) delete changes.v_temp;
        for (const [id, changes] of Object.entries(effects.ec)) if (!Object.keys(changes).length) delete effects.ec[id];
        if (Object.keys(effects.ec).length || effects.charges.length || reactionTransportFailure(effects, true))
          return 'lost_shapeshift:lane-cannot-persist';
      }
      const gate = resolveAttackDamageReactions({ actorId, targetId, damageTypes, damage: form?.vitalityDamage !== false, damageTotal: 0, weaponId: input.weaponId,
        zeroVitality: false, targetJustKilled: false, preview: true, rollPrivate: input.rollPrivate }, swingEncounter, members, gatedRead, { d: noDraw, fraction: noDraw });
      if (consentPending(gatedRead)) return 'preflight:trigger-consent';
      if (checkEffects && gate.kind === 'resolved') {
        const ctx = attackDamageFrame(swingEncounter, members, gatedRead, { d: noDraw, fraction: noDraw }, input.rollPrivate,
          frameCopyOnRead(swingEncounter, members, { catalogs: 'share' }));
        const gap = movementDamagePreflight(ctx, ctx.members.get(actorId)!, ctx.members.get(targetId)!, checkEffects);
        if (gap) return gap;
      }
      return gate.kind === 'resolved' ? null : 'attack:reactions:' + ('reason' in gate ? gate.reason : gate.message);
    }, applyInterSwingFortunate: luckAfterGain => {
      ec(currentAttacker).luck = luckAfterGain;
      applyUniversalFortunateOnLuckGain(ctx, currentAttacker);
    }, settleSwing: hit => {
      const victim = current.get(hit.targetId);
      if (!victim) throw new HandBack('attack:reaction-victim-not-held');
      if (!priced && hit.postRollFinal) { ec(currentAttacker).aps = hit.aps; priced = true; }
      Object.assign(ec(currentAttacker), { luck: hit.luck, reaction: hit.reaction });
      const luckBeforeSave = Number(ecLean(victim, 'luck') ?? 0);
      if (hit.maimedSave) ec(victim).luck = hit.maimedSave.luckAfter;
      Object.assign(ec(victim), { v_current: Math.max(0, hit.vitality), reaction: hit.targetReaction });
      if (hit.maimedSave?.natural === 20 && hit.maimedSave.luckAfter > Math.max(0, luckBeforeSave)
          && (isPlayerController(ctx, victim) || read.fortunateOnGain?.(victim, true)))
        applyUniversalFortunateOnLuckGain(ctx, victim);
      const vitality = record(record(victim.computed)?.vitality);
      if (vitality) vitality.v_current = Math.max(0, hit.vitality);
      const zero = hit.hit && hit.vitality <= 0;
      // Hit hooks already run in applyAttackDamageReactions. The miss has no
      // damage tail, but still owes the caster's authored outcome listeners.
      if (input.explicitInventoryWeapon && !hit.hit) {
        const event = { ...casterRollEvent, rollType: hit.rollType, excludeAbilitiesWithLayer: [], hit: false, criticalHit: false,
          restrictToAbilitiesWithLayer: ['triggers_on_miss', 'triggers_on_not_a_critical_hit'] };
        triggerFamilyPreDraw(ctx, currentAttacker, event, undefined, victim);
        runTriggerFamily(ctx, event, currentAttacker, victim, [currentAttacker], false);
      }
      applyAttackDamageReactions(ctx, { actorId: input.actorId, targetId: hit.targetId, damageTypes: hit.damageTypes,
        nextRollAgainstConsumed: hit.nextRollAgainstConsumed, damageBefore: hit.before, damageTotal: hit.damage,
        weaponId: hit.weaponId, targetCriticalMaimed: hit.maimedSave?.failed,
        damage: hit.before > hit.vitality, zeroVitality: zero, targetJustKilled: zero,
        ...(hit.hit ? { onHit: { weapon: read.definition(hit.weaponId) ?? weapon, rollType: hit.rollType, critical: hit.critical, skipTargetTail: !!input.opportunityAttack } } : {}),
        ...(zero ? { dying: { weaponId: hit.weaponId, damage: hit.damage, critical: hit.critical } } : {}),
        ...(!input.opportunityAttack && hit.postRollFinal ? { postRoll: { rollType: hit.rollType, hit: hit.hit } } : {}) });
      damageHealsCaster(ctx, { abilityId: hit.weaponId, casterId: input.actorId, targetIds: [hit.targetId],
        rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0,
        useAbilityVariable1: null, useAbilityVariable2: null }, read.definition(hit.weaponId) ?? weapon, currentAttacker, hit.casterHealing);
      if (consentPending(ctx.read)) throw new HandBack('outcome:trigger-consent');
      if (hit.paid && hit.hit && hit.hasRider && boundAbility) {
        applyExplicitWeaponHitEffects(ctx, boundAbility, currentAttacker, victim,
          { rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0 });
        paidAbilitySettled = true;
      }
      const dead = Number(ecLean(victim, 'encounter_creature_dead') ?? 0) === 1
        || Number(ecLean(victim, 'encounter_creature_deleted') ?? 0) === 1;
      const stop = dead || Number(ecLean(currentAttacker, 'encounter_creature_dead') ?? 0) === 1
        || Number(ecLean(currentAttacker, 'encounter_creature_deleted') ?? 0) === 1
        || Number(ecLean(currentAttacker, 'v_current') ?? 0) <= 0
        || activeStatuses(ctx, currentAttacker).some(status => {
          const definition = ctx.read.definition(statusItemId(status));
          return !!definition && Number(scalar(ctx.read.value(definition, input.opportunityAttack ? 'no_reactions' : 'no_actions')) ?? 0) > 0;
        });
      if (!stop && hit.hasRider) for (const [id, member] of current) {
        const original = originals.get(id)!;
        const target = activeStatuses(ctx, member).map(status => {
          const change = ctx.statusStacks?.find(entry => upper(entry.status_id) === upper(statusScalar(status, 'status_id')));
          return change ? { ...status, status_stack: change.status_stack } : status;
        });
        const key = statusKey(target);
        if (key === (projectedStatuses.get(id) ?? statusKey(statusRows(original, 'target')))) continue;
        projectedStatuses.set(id, key);
        // The projector replaces derived clusters; only the computed record needs its own writable shell.
        const projected: Row = { ...member, computed: { ...record(original.computed) }, statuses: original.statuses };
        const gap = projectAttackStatuses(projected, { ids: statusRows(original, 'target').map(s => String(statusScalar(s, 'status_id'))),
          target, caster: statusRows(member, 'caster') }, ctx.read.definition, current, ctx.read.possessorScene);
        if (gap === 'attack:status-projection:unported:status-ac:body') continue;
        if (gap) throw new HandBack(gap);
        member.computed = { ...record(projected.computed), vitality: record(record(member.computed)?.vitality) };
        if (projected.catalog) member.catalog = projected.catalog;
        // Pending statuses remain owned by ctx; activeStatuses includes them exactly once.
      }
      ctx.priorDispatchStatuses = new Set(ctx.statusesAdded);
      const after = Number(ecLean(victim, 'v_current') ?? 0);
      return { vitality: after, luck: Number(ecLean(currentAttacker, 'luck') ?? 0),
        targetFortunate: activeStatuses(ctx, victim).some(isFortunateStatus),
        targetReaction: Number(ecLean(victim, 'reaction') ?? 0), stop,
        death: dead ? (hit.damage >= Number(read.vitality(victim, 'v_max')) && read.itemType(victim) === 'player_character'
          ? 'massive_damage' : 'killed') : after <= 0 ? 'dying' : null };
    }, statusRollSources: true, swingRollSources: (target, weaponId, rollType, rider) => {
      const currentTarget = current.get(ecId(target)) ?? target;
      const types = [rollType, SKILL_ROLL_ALIAS], statusOnly = new Set(['status_effect', 'passive_ability']);
      // The primitive already folds the initial target's one-shot against modifier and owns its removal.
      const initialTargetCarry = (holder: Row, status: Row): boolean => {
        if (rider || ecId(holder) !== ecId(currentTarget)) return false;
        const definition = ctx.read.definition(statusItemId(status));
        return !!definition && nextRollAgainstRider(ctx.read, definition, 'target', new Set([rollType]),
          { usedRollType: rollType, rollerIsTarget: false }) !== null;
      };
      const heldModifiers = (holder: Row): Row[] => {
        const statuses = ctx.read.statuses(holder);
        // Reuse the damage source walk's condition/team gates and active-bucket dedup.
        const passives = statusItemPassiveEntries(holder, statuses.map(status =>
          ({ status, definition: ctx.read.definition(statusItemId(status)) })), {
          value: ctx.read.value, entries: ctx.read.entries, definition: ctx.read.definition,
          member: id => ctx.members.get(id),
          conditionScene: () => ctx.read.possessorScene?.(holder, [...ctx.members.values()])?.conditionScene?.() ?? { encounter: ctx.encounter, campaign: undefined },
          relevant: def => ['roll_type_adv', 'roll_type_dis'].some(key => ctx.read.entries(def, key)
            .some(entry => types.includes(layerListEntryUuid(entry)))),
        });
        if ('gap' in passives) throw new HandBack(passives.gap);
        const admitted = new Set(passives.entries.map(entry => entry.status));
        return statuses.filter(status => {
          const def = ctx.read.definition(statusItemId(status));
          return itemField(def, 'item_type') !== 'passive_ability' || isAttackPassiveWindow(def) || admitted.has(status);
        });
      };
      const modifierCtx = { ...ctx, read: { ...ctx.read,
        statuses: (holder: Row) => heldModifiers(holder).filter(status => rider || !initialTargetCarry(holder, status)) },
        statusesAdded: ctx.statusesAdded.filter(status => rider || !initialTargetCarry(current.get(status.target) ?? currentTarget, { ...status })) };
      const stacks = rollTypeModifierStacks(modifierCtx, currentAttacker, types, ['adv', 'dis'],
        { usedItemType: read.itemType(read.definition(weaponId)!), usedRollType: rollType, rollTo: ecId(currentTarget), opposing: currentTarget }, statusOnly)
        + rollTypeModifierStacks(modifierCtx, currentTarget, types, ['against_adv', 'against_dis'],
          { usedItemType: read.itemType(read.definition(weaponId)!), usedRollType: rollType, rollTo: input.actorId, opposing: currentAttacker }, statusOnly);
      for (const [holder, keys] of [[currentAttacker, ['triggers_on_next_roll_type']],
        [currentTarget, ['triggers_on_next_roll_type_against']]] as const) {
        if (!rider && holder === currentTarget) continue;
        for (const status of [...activeStatuses(ctx, holder)]) {
          const definition = ctx.read.definition(statusItemId(status));
          if (definition && keys.some(key => ctx.read.entries(definition, key).some(entry => types.includes(layerListEntryUuid(entry)))))
            removeStoredStatus(ctx, holder, status, 'single');
        }
      }
      if (consentPending(ctx.read)) throw new HandBack('outcome:trigger-consent');
      return stacks ? [{ kind: 'status_effect', label: 'Status Effects', stacks }] : [];
    }, statusRows: row => activeStatuses(ctx, row),
    hasStatus: (row, name) => name === 'dying'
      ? activeStatuses(ctx, row).some(s => statusItemId(s) === STATUS_IDS.dying) : scopedRead.hasStatus(row, name) };
    let scope: Scope & { damageEffects?: AttackReactionEffects };
    try {
      scope = resolveWeaponAttack(input, swingEncounter, currentAttacker, currentTarget, swingRead, source);
      if (damageEffects && scope.supported) {
        const effects = damageEffects, victimId = scope.outcome.stray?.targetId ?? input.targetId;
        const changes = effects.ec[victimId];
        const formVitalityChanged = changes && typeof changes.v_temp === 'number';
        if (changes && typeof changes.v_temp === 'number') {
          scope.deltas.target.v_temp = changes.v_temp;
          delete changes.v_temp;
          if (!Object.keys(changes).length) delete effects.ec[victimId];
        }
        if (Object.keys(effects.ec).length || effects.charges.length) return no('lost_shapeshift:lane-cannot-persist');
        const failure = reactionTransportFailure(effects, true) ?? pinAttackReactionRemovals(effects, originals, reactionRead);
        if (failure) return no('lost_shapeshift:lane-cannot-persist');
        if (formVitalityChanged || effects.rows.length || effects.statusesRemoved.length || effects.shapeshiftInventory?.length)
          scope = { ...scope, damageEffects: effects };
      }
    } catch (error) {
      if (error instanceof HandBack) return no('attack:reactions:' + error.reason);
      if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
      throw error;
    }
    if (scope.supported && boundAbility) {
      const victim = current.get(scope.outcome.stray?.targetId ?? input.targetId);
      if (!victim) return no('attack:reaction-victim-not-held');
      try {
        if (scope.outcome.hit && !paidAbilitySettled) applyExplicitWeaponHitEffects(ctx, boundAbility, currentAttacker, victim,
          { rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0 });
        spendExplicitWeaponCharges(ctx, boundAbility, currentAttacker, scope.outcome.hit);
      } catch (error) {
        if (error instanceof HandBack) return no('attack:reactions:' + error.reason);
        if (error instanceof Refusal) return { supported: false, refused: true, message: error.refusal };
        throw error;
      }
    }
    return finish(scope);
  };
  return run(roll);
}
